import { DependencyList, ReactElement, useCallback, useState } from 'react'
import { Typography } from '@equinor/eds-core-react'
import { useModal } from 'react-modal-hook'
import ConfirmDialog from '@/components/confirmDialog'

export interface BulletItem {
    text: string
}

export type DialogContent<TData> = string | ReactElement | ((data?: TData) => string | ReactElement)

export type DialogBullets<TData> = string[] | BulletItem[] | ((data?: TData) => string[] | BulletItem[])

export interface MovedDialogOptions<TData> {
    title?: string
    content1?: DialogContent<TData>
    content2?: DialogContent<TData>
    bullets?: DialogBullets<TData>
    onConfirm?: (data?: TData) => void
    onReject?: (data?: TData) => void
    rejectButtonTitle?: string
    confirmButtonTitle?: string
    rejectButtonDisabled?: boolean
    confirmButtonDisabled?: boolean
    isBusy?: boolean
    autoClose?: boolean
    onClose?: (data?: TData) => void
}

export const useEditMovedDialog = <TData extends unknown>(
    options: MovedDialogOptions<TData>,
    inputs?: DependencyList
) => {
    const [data, setData] = useState<TData>()

    const [showModal, hideModal] = useModal(() => {
        const content1 = typeof options.content1 === 'function' ? options.content1(data) : options.content1

        const content2 = typeof options.content2 === 'function' ? options.content2(data) : options.content2

        const bullets: Array<string | BulletItem> =
            typeof options.bullets === 'function' ? (options.bullets(data) ?? []) : (options.bullets ?? [])

        return (
            <ConfirmDialog
                title={options.title}
                onConfirmClick={onConfirmClick}
                onRejectClick={onRejectClick}
                rejectButtonTitle={options.rejectButtonTitle}
                confirmButtonTitle={options.confirmButtonTitle}
                rejectButtonDisabled={options.rejectButtonDisabled}
                confirmButtonDisabled={options.confirmButtonDisabled}
                isBusy={options.isBusy}
                onClose={onCloseClick}
                width="min(600px, calc(100vw - 32px))"
                maxWidth="min(800px, calc(100vw - 32px))"
            >
                <Typography variant="body_short" component="div">
                    {content1 &&
                        (typeof content1 === 'string' ? (
                            <div style={{ whiteSpace: 'pre-line' }}>{content1}</div>
                        ) : (
                            content1
                        ))}

                    {bullets.length > 0 && (
                        <ul className="list-disc pl-5 mt-2 space-y-1">
                            {bullets.map((b, i) => (
                                <li key={i}>{typeof b === 'string' ? b : b.text}</li>
                            ))}
                        </ul>
                    )}

                    {content2 && (
                        <div className="mt-2">
                            {typeof content2 === 'string' ? (
                                <div style={{ whiteSpace: 'pre-line' }}>{content2}</div>
                            ) : (
                                content2
                            )}
                        </div>
                    )}
                </Typography>
            </ConfirmDialog>
        )
    }, [data, inputs])

    const onConfirmClick = () => {
        options.onConfirm?.(data)
        if (options.autoClose === true || options.autoClose === undefined) {
            hideModal()
        }
    }

    const onCloseClick = () => {
        options.onClose?.(data)
        if (options.autoClose === true || options.autoClose === undefined) {
            hideModal()
        }
    }

    const onRejectClick = () => {
        options.onReject?.(data)
        if (options.autoClose === true || options.autoClose === undefined) {
            hideModal()
        }
    }

    const showConfirm = useCallback(
        (data?: TData) => {
            setData(data)
            showModal()
        },
        [showModal]
    )

    const hideConfirm = useCallback(() => {
        hideModal()
    }, [hideModal])

    return {
        showConfirm,
        hideConfirm,
    }
}
